// The worm level and the daily challenges: progress from every online round,
// whatever the mode (Play Now, the Royale, Ranked; never a game room or a
// local game, which could be farmed). Lives in core so the edge-server (which
// decides and pays) and the client (which shows it, and keeps a guest's on
// the device) read the same rules.
//
// Every round you steer in gives XP: a little for playing, more for the worms
// you outlasted, a round win, gaps threaded and crashes caused. XP fills the
// worm level. Three daily challenges (the same three for everyone, new every
// day) give XP too, and finishing all three pays a couple of Golden Eggs.
//
// Eggs are kept scarce on purpose: they are what the shop sells. A level pays
// eggs only at a few milestones; the level itself (and later the items it
// unlocks) is the reward.
// (Plain ES5: the core tests run in an old browser engine.)

export var ROUND_XP = {
    played: 5,
    outlasted: 1, outlastedMax: 20,
    won: 10,
    gap: 2, gapMax: 10,
    crash: 3, crashMax: 15,
};

// A round's facts for one player: { field (worms in the round), place (1 =
// last alive), steered (pressed a key at all: an idle worm earns nothing),
// gaps, crashes (worms that died on their trail), powerUps, alive (seconds),
// mode ("playnow" | "royale" | "duel" | "ffa") }.

// What a round is worth: { xp, parts: [{ id, xp, n? }] }, parts in the order
// the results screen lists them.
export function roundXp(r) {
    if (!r || !r.steered) {return { xp: 0, parts: [] };}
    var X = ROUND_XP;
    var parts = [{ id: "played", xp: X.played }];
    var outlasted = Math.max(0, (r.field | 0) - (r.place | 0));
    if (outlasted > 0) {parts.push({ id: "outlasted", n: outlasted, xp: Math.min(X.outlastedMax, outlasted * X.outlasted) });}
    if (r.place === 1) {parts.push({ id: "won", xp: X.won });}
    if (r.gaps > 0) {parts.push({ id: "gaps", n: r.gaps, xp: Math.min(X.gapMax, r.gaps * X.gap) });}
    if (r.crashes > 0) {parts.push({ id: "crashes", n: r.crashes, xp: Math.min(X.crashMax, r.crashes * X.crash) });}
    var xp = 0;
    parts.forEach(function (p) { xp += p.xp; });
    return { xp: xp, parts: parts };
}

// ---- the worm level ----

// XP from `level` to the next: 100, then 40 more for every level. Level 5 is
// about half an hour of play, level 10 a couple of hours, level 50 a long time.
export function xpToNext(level) {
    return 100 + 40 * (level - 1);
}

// { level, into (XP into this level), need (XP this level takes) } for a total.
export function levelOf(xp) {
    var level = 1, into = Math.max(0, xp | 0);
    while (into >= xpToNext(level)) {
        into -= xpToNext(level);
        level++;
    }
    return { level: level, into: into, need: xpToNext(level) };
}

// The few levels that pay eggs (everything up to 50 is 43 eggs).
export var LEVEL_EGGS = [
    { level: 5, eggs: 2 }, { level: 10, eggs: 3 }, { level: 15, eggs: 3 }, { level: 20, eggs: 5 },
    { level: 30, eggs: 5 }, { level: 40, eggs: 10 }, { level: 50, eggs: 10 },
];

// The egg levels passed going from `from` to `to`.
export function levelEggsBetween(from, to) {
    return LEVEL_EGGS.filter(function (l) { return l.level > from && l.level <= to; });
}

// ---- daily challenges ----

// tier 0 easy, 1 medium, 2 hard; a day has one of each, on different metrics.
// `text` is short (it fits one line on the start page); `detail`, when there
// is one, says the rest. "Crash" means into your trail.
export var CHALLENGES = [
    { id: "rounds-10", tier: 0, metric: "rounds", target: 10, text: "Play 10 rounds" },
    { id: "win-1", tier: 0, metric: "roundWins", target: 1, text: "Win a round" },
    { id: "gaps-3", tier: 0, metric: "gaps", target: 3, text: "Slip through 3 gaps" },
    { id: "powerups-5", tier: 0, metric: "powerUps", target: 5, text: "Grab 5 power-ups" },
    { id: "outlast-30", tier: 0, metric: "outlasted", target: 30, text: "Outlast 30 worms" },
    { id: "rounds-25", tier: 1, metric: "rounds", target: 25, text: "Play 25 rounds" },
    { id: "crashes-5", tier: 1, metric: "crashes", target: 5, text: "Make 5 worms crash", detail: "into your trail" },
    { id: "top3-3", tier: 1, metric: "top3", target: 3, text: "Top 3 in 3 rounds", detail: "in rounds of 6 or more worms" },
    { id: "powerups-15", tier: 1, metric: "powerUps", target: 15, text: "Grab 15 power-ups" },
    { id: "alive-45", tier: 1, metric: "longRounds", target: 3, text: "Survive 45 s, 3 times", detail: "stay alive for 45 seconds in 3 rounds" },
    { id: "win-5", tier: 2, metric: "roundWins", target: 5, text: "Win 5 rounds" },
    { id: "gaps-12", tier: 2, metric: "gaps", target: 12, text: "Slip through 12 gaps" },
    { id: "outlast-150", tier: 2, metric: "outlasted", target: 150, text: "Outlast 150 worms" },
    { id: "royale-6", tier: 2, metric: "royaleRounds", target: 6, text: "6 Royale rounds" },
    { id: "crashes-15", tier: 2, metric: "crashes", target: 15, text: "Make 15 worms crash", detail: "into your trail" },
];
export var CHALLENGE_XP = [100, 150, 250];
// Finishing all three of a day's challenges. They wait to be claimed (a click
// on the start page): the days are kept in the progress's `unclaimed`, at most
// UNCLAIMED_DAYS of them.
export var DAILY_EGGS = 2;
export var UNCLAIMED_DAYS = 7;

export function challengeById(id) {
    for (var i = 0; i < CHALLENGES.length; i++) {if (CHALLENGES[i].id === id) {return CHALLENGES[i];}}
    return null;
}

function hash(s) {
    var h = 5381;
    for (var i = 0; i < s.length; i++) {h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;}
    return h;
}

// The day's three ("YYYY-MM-DD"), easy first. Everyone gets the same three.
export function dailyChallenges(day) {
    var h = hash(String(day || ""));
    var picked = [], metrics = [];
    for (var tier = 0; tier < 3; tier++) {
        var pool = CHALLENGES.filter(function (c) { return c.tier === tier && metrics.indexOf(c.metric) === -1; });
        var c = pool[(h >>> (tier * 5)) % pool.length];
        picked.push(c);
        metrics.push(c.metric);
    }
    return picked;
}

// What a round adds to the day's counts (nothing for an idle round).
export function roundCounts(r) {
    if (!r || !r.steered) {return {};}
    return {
        rounds: 1,
        roundWins: r.place === 1 ? 1 : 0,
        gaps: r.gaps | 0,
        crashes: r.crashes | 0,
        powerUps: r.powerUps | 0,
        outlasted: Math.max(0, (r.field | 0) - (r.place | 0)),
        top3: r.field >= 6 && r.place <= 3 ? 1 : 0,
        longRounds: r.alive >= 45 ? 1 : 0,
        royaleRounds: r.mode === "royale" ? 1 : 0,
    };
}

// ---- a player's progress ----

export function emptyProgress() {
    return { xp: 0, day: null, counts: {}, done: [], allDone: false, unclaimed: [] };
}

function copy(p) {
    var out = emptyProgress();
    if (p) {
        out.xp = p.xp | 0;
        out.day = p.day || null;
        for (var k in (p.counts || {})) {out.counts[k] = p.counts[k];}
        out.done = (p.done || []).slice();
        out.allDone = !!p.allDone;
        out.unclaimed = (p.unclaimed || []).slice();
    }
    return out;
}

// The Golden Eggs waiting to be claimed for days whose three were all done.
export function claimableEggs(progress) {
    return ((progress && progress.unclaimed) || []).length * DAILY_EGGS;
}

// Fold one round into a player's progress on `day` (their own calendar day).
// Returns { progress, xp, parts, completed (challenges finished by this round),
// allDone (this round finished the day's last one), levelBefore, level,
// eggs (the egg levels passed: [{ level, eggs }]) }.
export function applyRound(progress, r, day) {
    var p = copy(progress);
    if (p.day !== day) {
        p.day = day;
        p.counts = {};
        p.done = [];
        p.allDone = false;
    }
    var got = roundXp(r);
    var xp = got.xp;
    var parts = got.parts.slice();
    var counts = roundCounts(r);
    for (var k in counts) {p.counts[k] = (p.counts[k] || 0) + counts[k];}
    var completed = [];
    dailyChallenges(day).forEach(function (c) {
        if (p.done.indexOf(c.id) !== -1 || (p.counts[c.metric] || 0) < c.target) {return;}
        p.done.push(c.id);
        completed.push(c);
        parts.push({ id: "challenge", challenge: c.id, xp: CHALLENGE_XP[c.tier] });
        xp += CHALLENGE_XP[c.tier];
    });
    var allDone = !p.allDone && p.done.length >= 3;
    if (allDone) {
        p.allDone = true;
        if (p.unclaimed.indexOf(day) === -1) {p.unclaimed = p.unclaimed.concat([day]).slice(-UNCLAIMED_DAYS);}
    }
    var before = levelOf(p.xp).level;
    p.xp += xp;
    var after = levelOf(p.xp).level;
    return { progress: p, xp: xp, parts: parts, completed: completed, allDone: allDone, levelBefore: before, level: after, eggs: levelEggsBetween(before, after) };
}

// The day's challenges with how far `progress` is on them (a progress from
// another day counts as nothing yet): [{ ...challenge, have, done, xp }].
export function challengeStatus(progress, day) {
    var same = progress && progress.day === day;
    return dailyChallenges(day).map(function (c) {
        var have = same ? (progress.counts[c.metric] || 0) : 0;
        var done = same && (progress.done || []).indexOf(c.id) !== -1;
        return { id: c.id, tier: c.tier, metric: c.metric, target: c.target, text: c.text, detail: c.detail || null, have: Math.min(have, c.target), done: done, xp: CHALLENGE_XP[c.tier] };
    });
}
